import React, { ReactNode } from 'react'
import DownIcons from '@/icons/DownIcons'

interface ExpandableAlertBannerProps {
    /** Icon displayed on the left side of the banner */
    icon: ReactNode
    /** Title text */
    title: ReactNode
    /** Description text */
    description: ReactNode
    /** Whether the banner is expanded */
    isExpanded: boolean
    /** Callback when banner is clicked to expand/collapse */
    onToggleExpand: () => void
    /** Callback when close button is clicked */
    onClose: (e: React.MouseEvent) => void
    /** Background color class (default: bg-amber-50) */
    backgroundColor?: string
    /** Accent stripe color class (default: bg-orange-500) */
    stripeColor?: string
    /** Text color class (default: text-[#8B4513]) */
    textColor?: string
    /** Icon color class (default: text-orange-500) */
    iconColor?: string
    /** Close button aria label */
    closeAriaLabel?: string
    /** Additional className for the banner */
    className?: string
}

const ExpandableAlertBanner: React.FC<ExpandableAlertBannerProps> = ({
    icon,
    title,
    description,
    isExpanded,
    onToggleExpand,
    backgroundColor = 'bg-amber-50',
    stripeColor = 'bg-orange-500',
    textColor = 'text-[#8B4513]',
    iconColor = 'text-orange-500',
    className = '',
}) => {
    return (
        <div
            className={`relative ${backgroundColor} rounded-lg overflow-hidden cursor-pointer transition-all duration-200 hover:shadow-md ${className}`}
            onClick={onToggleExpand}
        >
            {/* Accent vertical stripe */}
            <div
                className={`absolute left-0 top-0 bottom-0 w-1 ${stripeColor}`}
            />

            <div className="flex items-start gap-4 p-4 pr-16">
                {/* Left Icon */}
                <div className="flex-shrink-0 mt-0.5">{icon}</div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                    <div className={`font-bold ${textColor} text-lg mb-1`}>
                        {title}
                    </div>
                    <div className={`${textColor} text-sm`}>{description}</div>
                </div>
            </div>

            {/* Right side icons - X and Chevron */}
            <div className="absolute top-4 right-4 flex flex-col items-center gap-2">
                <DownIcons
                    className={`w-5 h-5 ${iconColor} transition-transform duration-200 ${
                        isExpanded ? 'rotate-180' : ''
                    }`}
                />
            </div>
        </div>
    )
}

export default ExpandableAlertBanner
